import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/context/I18nContext";
import api, { ROLE_LABELS, isInternal } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, Receipt, FileBarChart, Database, Lock,
  Users, History, LogOut, Bell, Moon, Sun, Menu, Wallet, FolderArchive, FolderOpen, Languages, BookOpen, FileSpreadsheet, DatabaseBackup, TrendingUp, Sparkles, Building2, ClipboardList, Check, ChevronsUpDown, Landmark, ReceiptText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { fl } from "@/lib/finI18n";
import { ClientLogo } from "@/components/ClientLogo";
import { ScrollBall } from "@/components/ScrollBall";
import { useMainScroll } from "@/lib/useMainScroll";

const NAV = [
  { to: "/", key: "nav.dashboard", tid: "dashboard", icon: LayoutDashboard, roles: "all" },
  { to: "/transaksi", key: "nav.transactions", tid: "transaksi", icon: Receipt, roles: "all" },
  { to: "/laporan", key: "nav.reports", tid: "laporan", icon: FileBarChart, roles: "all" },
  { to: "/payroll", key: "nav.payroll", tid: "payroll", icon: Wallet, roles: "all" },
  { to: "/aset", key: "nav.assets", tid: "aset", icon: Landmark, roles: "all" },
  { to: "/master", key: "nav.master", tid: "master", icon: Database, roles: "all" },
  { to: "/jurnal", key: "nav.journal", tid: "jurnal", icon: BookOpen, roles: "internal" },
  { to: "/invoice", key: "nav.invoices", tid: "invoice", icon: FileSpreadsheet, roles: "all" },
  { to: "/faktur", key: "nav.faktur", tid: "faktur", icon: ReceiptText, roles: "all" },
  { to: "/klien", key: "nav.clients", tid: "klien", icon: Building2, roles: "internal" },
  { to: "/proyek", key: "nav.projects", tid: "proyek", icon: ClipboardList, roles: "internal" },
  { to: "/ai", key: "nav.ai", tid: "ai", icon: Sparkles, roles: "all" },
  { to: "/arsip", key: "nav.archive", tid: "arsip", icon: FolderArchive, roles: "all" },
  { to: "/dokumen", key: "nav.documents", tid: "dokumen", icon: FolderOpen, roles: "all" },
  { to: "/data", key: "nav.data", tid: "data", icon: DatabaseBackup, roles: ["super_admin", "supervisor", "staff_keuangan", "staff_pajak", "leader"] },
  { to: "/periode", key: "nav.periods", tid: "periode", icon: Lock, roles: ["super_admin", "supervisor"] },
  { to: "/pengguna", key: "nav.users", tid: "pengguna", icon: Users, roles: ["super_admin"] },
  { to: "/audit", key: "nav.audit", tid: "audit", icon: History, roles: "internal" },
];

function ClientSwitcher({ clients, value, onSelect }) {
  const [open, setOpen] = useState(false);
  const current = clients.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className="w-36 sm:w-52 h-9 justify-between font-normal" data-testid="client-switcher">
          <span className="flex items-center gap-2 min-w-0">{current && <ClientLogo id={current.id} v={current.logo_v} className="h-5 w-5" icon="h-3 w-3" />}<span className="truncate">{current?.name || "Pilih Klien"}</span></span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Cari klien..." data-testid="client-switcher-search" />
          <CommandList>
            <CommandEmpty>Klien tidak ditemukan.</CommandEmpty>
            <CommandGroup>
              {clients.map((c) => (
                <CommandItem key={c.id} value={`${c.name} ${c.code}`}
                  onSelect={() => { onSelect(c.id); setOpen(false); }}
                  data-testid={`client-option-${c.code}`}>
                  <Check className={cn("mr-2 h-4 w-4", value === c.id ? "opacity-100" : "opacity-0")} />
                  <ClientLogo id={c.id} v={c.logo_v} className="h-5 w-5 mr-2" icon="h-3 w-3" />
                  <span className="truncate">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children }) {  const { user, logout, clients, selectedClientId, selectClient, reloadUser } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [dark, setDark] = useState(localStorage.getItem("fiskal_dark") === "1");
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const mainRef = useRef(null);
  useMainScroll(mainRef);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("fiskal_dark", dark ? "1" : "0");
  }, [dark]);

  const loadNotif = async () => {
    try { const { data } = await api.get("/notifications"); setNotif(data); } catch (e) {}
  };
  useEffect(() => {
    loadNotif();
    const t = setInterval(loadNotif, 30000);
    return () => clearInterval(t);
  }, []);

  const readNotif = async () => {
    await api.post("/notifications/read");
    loadNotif();
  };

  const visibleNav = NAV.filter((n) =>
    n.roles === "all" ? true : n.roles === "internal" ? isInternal(user.role) : n.roles.includes(user.role)
  );

  return (
    <div className="h-[100dvh] flex overflow-hidden bg-background text-foreground">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`${sidebarOpen ? "w-60" : "w-0"} h-full overflow-y-auto overflow-x-hidden transition-all duration-200 shrink-0 border-r border-border bg-card hidden md:block`}
      >
        <div className="sticky top-0 z-10 h-16 flex items-center gap-2 px-5 border-b border-border bg-card">
          <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-head font-extrabold">F</div>
          <div>
            <div className="font-head font-bold text-sm leading-none">Fiskal <span className="font-light opacity-70">By SAVIOR</span></div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{t("app.tagline")}</div>
          </div>
        </div>
        <nav className="p-3 space-y-1">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.tid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {t(n.key)}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        <header className="h-16 shrink-0 z-30 border-b border-border bg-card flex items-center justify-between px-2 sm:px-4 gap-2 sm:gap-4" data-testid="app-header">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="icon" data-testid="toggle-sidebar" onClick={() => setSidebarOpen((v) => !v)}>
              <Menu className="h-5 w-5" />
            </Button>
            {isInternal(user.role) && clients.length > 0 && (
              <ClientSwitcher clients={clients} value={selectedClientId} onSelect={selectClient} />
            )}
            {!isInternal(user.role) && clients[0] && (
              <span className="text-sm font-semibold">{clients[0].name}</span>
            )}
          </div>

          <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="w-auto sm:w-[120px] h-9 gap-1 px-2 sm:px-3" data-testid="lang-switcher">
                <Languages className="h-4 w-4" />
                <span className="!hidden sm:!block"><SelectValue /></span>
              </SelectTrigger>
              <SelectContent>
                {LANGS.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`lang-${l.code}`}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <DropdownMenu onOpenChange={(o) => o && readNotif()}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notif-btn">
                  <Bell className="h-5 w-5" />
                  {notif.unread > 0 && (
                    <span className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] flex items-center justify-center">
                      {notif.unread}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>{t("header.notifications")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notif.items.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">{t("header.noNotif")}</div>
                )}
                {notif.items.slice(0, 8).map((n) => (
                  <div key={n.id} className={`px-3 py-2 text-xs border-b border-border last:border-0 ${n.transaction_id || n.link ? "cursor-pointer hover:bg-secondary" : ""}`} data-testid="notif-item"
                    onClick={() => { if (n.link) navigate(n.link); else if (n.transaction_id) navigate(`/transaksi?txn=${n.transaction_id}`); }}>
                    <p className="text-foreground">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("id-ID")}</p>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" size="icon" data-testid="dark-toggle" onClick={() => setDark((v) => !v)}>
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 pl-2" data-testid="user-menu">
                  <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                    {user.name?.charAt(0)}
                  </div>
                  <div className="text-left hidden sm:block">
                    <div className="text-xs font-semibold leading-none">{user.name}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{ROLE_LABELS[user.role]}</div>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem checked={user.kmk_email !== false} data-testid="pref-kmk-email"
                  onCheckedChange={async (v) => { await api.put("/auth/me/prefs", { kmk_email: !!v }); reloadUser(); }}>
                  {fl("kmkEmailPref", lang)}
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}>
                  <LogOut className="h-4 w-4 mr-2" /> {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main ref={mainRef} className="flex-1 overflow-auto p-4 pb-24 md:p-6 md:pb-24" data-testid="app-main">{children}</main>
        <ScrollBall target={mainRef} />
      </div>
    </div>
  );
}
